import { ActionsQuery } from 'generated/resources/graphql'
import moment from 'moment'

import type { PopoverMenuTouchableOptionProps as Option } from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { stringToDateWithTimezone } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ActionsEmployeeData } from 'modules/employees/components/Actions/types'
import { useUnterminateEmployee } from 'modules/employees/hooks/useUnterminateEmployee'

const invitedOptions = (
  isCurrent: boolean,
  onClickUninvite: () => void,
  canInvite: boolean
): Option[] => {
  if (isCurrent) return [] // You should not be able to uninvite yourself

  if (!canInvite) return []

  return [
    {
      onClick: onClickUninvite,
      title: i18n.t('employees.uninvite.action'),
      description: i18n.t('employees.uninvite.action_description'),
    },
  ]
}

const unterminatedOptions = (
  employeeData: ActionsEmployeeData,
  onClickTerminate: () => void,
  onClickBecome: () => void,
  onClickDisableMfa: () => void,
  onClickSetWelcomeSpaceAccess: () => void,
  canBecome: boolean,
  canDisableMfa: boolean | undefined,
  canTerminate: boolean,
  canInvite: boolean,
  hasEmployeeOnboarding: boolean
): Option[] => {
  const canLogin = employeeData.access.isConfirmed && employeeData.joined
  const name = employeeData.firstName

  const actions = []
  const hasRegularAccess = hasEmployeeOnboarding || employeeData.hasRegularAccess

  if (canInvite && !canLogin && hasRegularAccess) {
    actions.push({
      onClick: onClickSetWelcomeSpaceAccess,
      title: i18n.t('employees.welcome_space.action'),
      description: i18n.t('employees.welcome_space.action_description'),
    })
  }

  if (canBecome) {
    actions.push({
      onClick: onClickBecome,
      title: i18n.t('employees.become.action', {
        name,
      }),
      description: i18n.t('employees.become.action_description', {
        name,
      }),
    })
  }

  if (canDisableMfa) {
    actions.push({
      onClick: onClickDisableMfa,
      title: i18n.t('employees.disable_mfa.action', {
        name,
      }),
      description: i18n.t('employees.disable_mfa.action_description'),
    })
  }

  if (canTerminate) {
    actions.push({
      onClick: onClickTerminate,
      title: i18n.t('employees.termination.action', {
        name,
      }),
      description: i18n.t('employees.termination.action_description'),
      danger: true,
    })
  }

  return actions
}

interface Props {
  employeeData: ActionsEmployeeData
  queryData: ActionsQuery | undefined
  onClickUninvite: () => void
  onClickTerminate: () => void
  onClickBecome: () => void
  onClickDisableMfa: () => void | undefined
  onClickSetWelcomeSpaceAccess: () => void
  onClickActivate: () => void
}

const useOptions = ({
  employeeData,
  queryData,
  onClickUninvite,
  onClickTerminate,
  onClickBecome,
  onClickDisableMfa,
  onClickSetWelcomeSpaceAccess,
  onClickActivate,
}: Props): Option[] => {
  let options: Option[] = []
  const { unterminate } = useUnterminateEmployee()
  const {
    access: { id: currentAccessId },
  } = useCurrentSession()
  const isCurrentUser = currentAccessId === employeeData.access.id

  async function unterminateEmployee() {
    const { data, error } = await unterminate({ id: employeeData.id })

    if (data?.employee?.id) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: error || i18n.t('common.save_error'),
        time: 2000,
      })
    }
  }

  const terminatedOptions = (): Option[] => {
    const terminatedOn = employeeData.terminatedOn
      ? stringToDateWithTimezone(employeeData.terminatedOn, 'backend')
      : null
    const isTerminated = terminatedOn && terminatedOn.isBefore(moment())
    const onClickUnterminate = async () => {
      if (isTerminated) {
        onClickActivate() // open ActivateModal to select type of access
      } else {
        await unterminateEmployee()
      }
    }

    const description = isTerminated
      ? i18n.t('employees.untermination.action_description')
      : i18n.t('employees.stop_termination.action_description')

    return [
      {
        onClick: onClickUnterminate,
        title: i18n.t('employees.untermination.action'),
        description: description,
      },
    ]
  }

  const employeeTerminated = employeeData.terminatedOn
  const canBecome = queryData?.employees.employee?.access?._policies?.become.can ?? false
  const canDisableMfa =
    queryData?.employees.employee?.authenticationAccess?._policies?.disableMfa.can ?? false
  const mfaEnabled = queryData?.employees.employee?.authenticationAccess?.mfaEnabled ?? false
  const canTerminate = queryData?.employees.employee?._policies?.terminateEmployees.can ?? false
  const canInvite = queryData?.employees.employee?._policies?.inviteEmployeeToApp.can ?? false
  const hasEmployeeOnboarding =
    queryData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'employee_onboarding'
    )?.enabled ?? false

  const invited = !!employeeData.access.invitedOn
  const joined = employeeData.joined
  if (!employeeTerminated && invited && !joined) {
    options = [...options, ...invitedOptions(isCurrentUser, onClickUninvite, canInvite)]
  }

  if (employeeTerminated) {
    options = [...options, ...terminatedOptions()]
  } else if (!isCurrentUser) {
    options = [
      ...options,
      ...unterminatedOptions(
        employeeData,
        onClickTerminate,
        onClickBecome,
        onClickDisableMfa,
        onClickSetWelcomeSpaceAccess,
        canBecome,
        canDisableMfa && mfaEnabled,
        canTerminate,
        canInvite,
        hasEmployeeOnboarding
      ),
    ]
  }
  return options
}

export default useOptions
